import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  SunIcon,
  UserCircleIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  CurrencyPoundIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  Cog6ToothIcon,
  MapIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import {
  clearEngineerSession,
  engineerService,
  getEngineerUser,
  setEngineerSession,
  getEngineerToken,
  EngineerUser,
} from '../../services/engineerService';
import PortalShell, { PortalNavGroup } from '../../components/Layout/PortalShell';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationTray from '../../components/Notifications/NotificationTray';

const EngineerLayout: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<EngineerUser | null>(getEngineerUser());
  const { unreadCount } = useNotifications();

  // Hydrate latest profile + company (logo, name) on mount so the sidebar
  // reflects fresh data even after the initial login response.
  useEffect(() => {
    if (!getEngineerToken()) return;
    let cancelled = false;
    engineerService
      .me()
      .then((me) => {
        if (cancelled) return;
        const cached = getEngineerUser();
        const merged: EngineerUser = {
          id: me.id,
          email: me.email,
          name: me.name,
          role: me.role,
          companyId: me.company?.id || cached?.companyId || '',
          companyName: me.company?.name || cached?.companyName,
          companyLogoUrl: me.company?.logoUrl ?? cached?.companyLogoUrl ?? null,
        };
        setEngineerSession(getEngineerToken(), merged);
        setUser(merged);
      })
      .catch(() => {
        /* ignore - interceptor handles 401 */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = () => {
    clearEngineerSession();
    navigate('/engineer/login', { replace: true });
  };

  const groups: PortalNavGroup[] = [
    {
      title: 'SCHEDULE',
      items: [
        { name: 'Today', href: '/engineer/today', icon: SunIcon },
        { name: 'Upcoming', href: '/engineer/upcoming', icon: CalendarDaysIcon },
        { name: 'Route', href: '/engineer/route', icon: MapIcon },
      ],
    },
    {
      title: 'WORK',
      items: [
        { name: 'Jobs', href: '/engineer/jobs', icon: BriefcaseIcon },
        {
          name: 'Certificates',
          href: '/engineer/certificates',
          icon: DocumentCheckIcon,
        },
        { name: 'Quotes', href: '/engineer/quotes', icon: DocumentTextIcon },
        { name: 'Invoices', href: '/engineer/invoices', icon: CurrencyPoundIcon },
      ],
    },
    {
      title: 'INBOX',
      items: [
        {
          name: 'Messages',
          href: '/engineer/messages',
          icon: ChatBubbleLeftRightIcon,
        },
        {
          name: 'Notifications',
          href: '/engineer/notifications',
          icon: BellIcon,
          badge: unreadCount > 0 ? unreadCount : undefined,
        },
      ],
    },
    {
      title: 'ME',
      items: [
        {
          name: 'Documents',
          href: '/engineer/documents',
          icon: IdentificationIcon,
        },
        { name: 'Profile', href: '/engineer/profile', icon: UserCircleIcon },
        { name: 'Settings', href: '/engineer/settings', icon: Cog6ToothIcon },
      ],
    },
  ];

  const brandName = user?.companyName || 'Engineer';
  const brandInitial = (user?.companyName || 'E')[0];

  return (
    <PortalShell
      brand={{
        name: brandName,
        logoUrl: user?.companyLogoUrl || null,
        initial: brandInitial,
      }}
      user={{
        name: user?.name,
        email: user?.email,
        subtitle: user?.companyName ? 'Field engineer' : undefined,
      }}
      groups={groups}
      onLogout={logout}
      renderNotificationPanel={({ open, onClose }) => (
        <NotificationTray isOpen={open} onClose={onClose} />
      )}
    >
      <Outlet />
    </PortalShell>
  );
};

export default EngineerLayout;
